import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState, useCallback, useRef } from 'react';
import { conversationApi, type ConversationDetail, type ConversationMessage } from '@/lib/api';
import {
  ArrowLeft, MessageSquare, User, Bot, AlertTriangle,
  Zap, Clock, Hash, Circle, KeyRound, ChevronDown, ChevronRight, ExternalLink,
} from 'lucide-react';
import styles from './_auth.conversations.$conversationId.module.css';
import { MarkdownMessage } from '@/components/ui/MarkdownMessage';
import { usePortalSocketEvent } from '@/contexts/PortalSocketContext';

export const Route = createFileRoute('/_auth/conversations/$conversationId')({
  component: ConversationDetailPage,
});

const EVENT_COLOR: Record<string, string> = {
  CONVERSATION_STARTED: 'var(--color-cyan)',
  MESSAGE_RECEIVED: 'var(--color-primary)',
  MODEL_REQUEST_STARTED: 'var(--color-warning)',
  MODEL_REQUEST_COMPLETED: 'var(--color-success)',
  MODEL_REQUEST_FAILED: 'var(--color-error)',
  MESSAGE_SENT: 'var(--color-success)',
  HANDOFF_CREATED: 'var(--color-error)',
  CONVERSATION_RESOLVED: 'var(--color-success)',
  CONVERSATION_ABANDONED: 'var(--color-muted)',
};

const STATUS_COLOR: Record<string, string> = {
  OPEN: 'var(--color-success)',
  WAITING_FOR_AGENT: 'var(--color-error)',
  WAITING_FOR_USER: 'var(--color-warning)',
  RESOLVED: 'var(--color-muted)',
  ABANDONED: 'var(--color-muted)',
};

export default function ConversationDetailPage() {
  const { conversationId } = Route.useParams();
  const [conv, setConv] = useState<ConversationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [contextExpanded, setContextExpanded] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    conversationApi.get(conversationId)
      .then(setConv)
      .finally(() => setLoading(false));
  }, [conversationId]);

  // ── Realtime: append new messages as they arrive ─────────────────────────
  const handleMessageNew = useCallback((data: unknown) => {
    const payload = data as { conversationId?: string; message?: ConversationMessage; senderType?: string; content?: string };
    // Only handle if it's for this conversation
    if (payload.conversationId && payload.conversationId !== conversationId) return;
    const msg = payload.message ?? (payload.senderType ? payload as unknown as ConversationMessage : null);
    if (!msg) return;
    setConv(prev => {
      if (!prev) return prev;
      // Avoid duplicates
      if (prev.messages.some(m => m.id === (msg as ConversationMessage).id)) return prev;
      return { ...prev, messages: [...prev.messages, msg as ConversationMessage] };
    });
    // Scroll to bottom
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, [conversationId]);

  usePortalSocketEvent('message:new', handleMessageNew);

  if (loading) {
    return <div className={styles.loading}><MessageSquare size={28} /><span>Loading…</span></div>;
  }

  if (!conv) {
    return <div className={styles.loading}><AlertTriangle size={28} /><span>Conversation not found</span></div>;
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <Link to="/conversations" className={styles.back}><ArrowLeft size={16} /> Conversations</Link>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Conversation</h1>
          <code className={styles.convId}>{conv.id.slice(0, 16)}…</code>
          <span className={styles.statusBadge} style={{ color: STATUS_COLOR[conv.status] }}>
            <Circle size={7} fill="currentColor" />{conv.status.replace(/_/g, ' ')}
          </span>
          {/* Open in new tab */}
          <a
            href={`/conversations/${conv.id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 7, fontSize: 12, fontWeight: 600, color: '#6366f1', textDecoration: 'none', background: '#6366f110', border: '1px solid #6366f130' }}
          >
            <ExternalLink size={12} /> Open in new tab
          </a>
        </div>
        <div className={styles.meta}>
          <span>Agent: <strong>{conv.agent?.name ?? 'None'}</strong></span>
          <span>Channel: <strong>{conv.channel}</strong></span>
          <span>Turns: <strong>{conv.turnCount}</strong></span>
          <span>Started: <strong>{new Date(conv.createdAt).toLocaleString()}</strong></span>
          {conv.handoff && <span className={styles.handoffBadge}><AlertTriangle size={12} /> Handed off</span>}
        </div>

        {/* Session Context panel — shown to operators for debugging */}
        {(conv as unknown as { sessionContext?: Record<string, unknown> }).sessionContext && (
          <div style={{
            marginTop: 10, border: '1px solid #6366f130', borderRadius: 8,
            background: '#6366f108', overflow: 'hidden',
          }}>
            <button
              onClick={() => setContextExpanded(e => !e)}
              style={{
                width: '100%', padding: '8px 14px', background: 'none', border: 'none',
                display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
                color: '#a5b4fc', fontSize: 12, fontWeight: 600,
              }}
            >
              {contextExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              <KeyRound size={12} />
              Session Context
              <span style={{ color: '#6366f1', marginLeft: 'auto', fontSize: 11 }}>
                {Object.keys((conv as unknown as { sessionContext: Record<string, unknown> }).sessionContext).length} keys
              </span>
            </button>
            {contextExpanded && (
              <div style={{ padding: '0 14px 12px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {Object.entries((conv as unknown as { sessionContext: Record<string, unknown> }).sessionContext).map(([k, v]) => (
                  <span key={k} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '2px 8px', borderRadius: 5,
                    background: '#6366f118', border: '1px solid #6366f130',
                    fontSize: 11, fontFamily: 'monospace',
                  }}>
                    <span style={{ color: '#818cf8' }}>{k}</span>
                    <span style={{ color: 'var(--color-muted)' }}>:</span>
                    <span style={{ color: '#e2e8f0' }}>{String(v)}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className={styles.layout}>
        {/* Transcript */}
        <div className={styles.transcript}>
          <h2 className={styles.sectionTitle}><MessageSquare size={15} /> Transcript</h2>
          <div className={styles.messages}>
            {conv.messages.map(m => (
              <div key={m.id} className={`${styles.message} ${styles[`msg_${m.senderType.toLowerCase()}`]}`}>
                <div className={styles.msgSender}>
                  {m.senderType === 'USER' && <User size={14} />}
                  {m.senderType === 'AGENT' && <Bot size={14} />}
                  {m.senderType === 'SYSTEM' && <AlertTriangle size={14} />}
                  <span>{m.senderType}</span>
                  <span className={styles.msgTime}>{new Date(m.createdAt).toLocaleTimeString()}</span>
                </div>
                <div className={styles.msgBubble}>
                  {/* Agent messages render as rich markdown; user messages are plain text */}
                  {m.senderType === 'AGENT' || m.senderType === 'SYSTEM'
                    ? <MarkdownMessage content={m.content} />
                    : m.content
                  }
                </div>
              </div>
            ))}
            {conv.messages.length === 0 && (
              <p className={styles.empty}>No messages yet.</p>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Event timeline */}
        <div className={styles.timeline}>
          <h2 className={styles.sectionTitle}><Hash size={15} /> Event Timeline</h2>
          <div className={styles.events}>
            {conv.events.map((ev, i) => (
              <div key={ev.id} className={styles.eventItem}>
                <div className={styles.eventLine}>
                  <div className={styles.eventDot} style={{ background: EVENT_COLOR[ev.eventType] ?? 'var(--color-muted)' }} />
                  {i < conv.events.length - 1 && <div className={styles.eventConnector} />}
                </div>
                <div className={styles.eventContent}>
                  <div className={styles.eventType}>{ev.eventType.replace(/_/g, ' ')}</div>
                  <div className={styles.eventMeta}>
                    <span className={styles.metaChip}>#{ev.sequenceNumber}</span>
                    {ev.durationMs != null && (
                      <span className={styles.metaChip}><Clock size={10} /> {ev.durationMs}ms</span>
                    )}
                    {ev.costUnits != null && ev.costUnits > 0 && (
                      <span className={styles.metaChip}><Zap size={10} /> {ev.costUnits} tok</span>
                    )}
                  </div>
                  {ev.payload && Object.keys(ev.payload).length > 0 && (
                    <pre className={styles.payload}>{JSON.stringify(ev.payload, null, 2)}</pre>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
